import { MenuItem, Select } from '@mui/material'
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import LoadingIndicator from '../../common/LoadingIndicator'

export default function UserStats({ driverStats, companionStats, loading }) {
  const timeframeOrder = ['1w', '1m', '2m', '6m', 'all']
  const available = timeframeOrder.filter(
    k => (driverStats && driverStats[k]) || (companionStats && companionStats[k])
  )
  const defaultTf = available.includes('1m') ? '1m' : available[0] || 'all'

  const [range, setRange] = useState(defaultTf)
  useEffect(() => {
    setRange(defaultTf)
  }, [defaultTf])

  const labelForRange = key => {
    switch (key) {
      case '1w':
        return '1 semana'
      case '1m':
        return '1 mes'
      case '2m':
        return '2 meses'
      case '6m':
        return '6 meses'
      case 'all':
      default:
        return 'Todo'
    }
  }

  const formatNumber = value => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
    const num = Number(value)
    if (Number.isInteger(num)) return num.toLocaleString('es-UY')
    return num.toLocaleString('es-UY', { maximumFractionDigits: 1 })
  }
  const formatPercent = value =>
    value === null || value === undefined || Number.isNaN(Number(value))
      ? '-'
      : `${Number(value).toLocaleString('es-UY', { maximumFractionDigits: 1 })}%`

  // Helper para determinar nivel de alerta
  const getAlertLevel = value => {
    const num = Number(value)
    if (isNaN(num) || num === 0) return null
    if (num === 1) return 'yellow'
    if (num === 2) return 'orange'
    return 'red' // 3 o más
  }

  // Helper para obtener clases de color según nivel
  const getAlertColorClass = level => {
    switch (level) {
      case 'yellow':
        return 'text-yellow-500 bg-yellow-50 dark:text-amber-400 dark:bg-amber-500/10'
      case 'orange':
        return 'text-orange-500 bg-orange-50 dark:text-orange-400 dark:bg-orange-500/10'
      case 'red':
        return 'text-red-500 bg-red-50 animate-pulse dark:text-red-400 dark:bg-red-500/10'
      default:
        return ''
    }
  }

  // Helper para obtener color del icono
  const getAlertIconClass = level => {
    switch (level) {
      case 'yellow':
        return 'text-yellow-500 dark:text-amber-400'
      case 'orange':
        return 'text-orange-500 dark:text-orange-400'
      case 'red':
        return 'text-red-500 animate-pulse dark:text-red-400'
      default:
        return ''
    }
  }

  // Keys que deben mostrar alerta
  const alertKeys = ['cancel_with_pax', 'expired_requests', 'rejected_requests']

  // Calcula el nivel máximo de alerta para el título
  const getMaxAlertLevel = data => {
    if (!data) return null
    let maxLevel = null
    for (const key of alertKeys) {
      const level = getAlertLevel(data[key])
      if (level === 'red') return 'red'
      if (level === 'orange' && maxLevel !== 'red') maxLevel = 'orange'
      if (level === 'yellow' && !maxLevel) maxLevel = 'yellow'
    }
    return maxLevel
  }

  const DriverCard = ({ data }) => {
    if (!data) return <p className='text-gray dark:text-gray-400'>Sin datos</p>
    const rows = [
      { label: 'Publicados', key: 'published' },
      { label: 'Completados', key: 'completed' },
      { label: 'Cancelados con pax', key: 'cancel_with_pax', alert: true },
      { label: 'Cancelados sin pax', key: 'cancel_without_pax' },
      { label: 'Solicitudes expiradas', key: 'expired_requests', alert: true },
      { label: 'Solicitudes rechazadas', key: 'rejected_requests', alert: true },
      { label: 'Conversión', key: 'conversion', format: formatPercent },
      { label: 'Tasa de cancelación', key: 'cancellation_rate', format: formatPercent },
      { label: 'Km', key: 'km' }
    ]

    const maxAlertLevel = getMaxAlertLevel(data)

    return (
      <div className='border-[1px] border-gray rounded-lg p-5 dark:border-dk-border'>
        <div className='flex items-center gap-2 mb-3'>
          <p className='font-bold'>Conductor</p>
          {maxAlertLevel && (
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className={getAlertIconClass(maxAlertLevel)}
            />
          )}
        </div>
        <div className='flex flex-col gap-2'>
          {rows.map(r => {
            const alertLevel = r.alert ? getAlertLevel(data[r.key]) : null
            const colorClass = alertLevel ? getAlertColorClass(alertLevel) : ''

            return (
              <div key={r.key} className={`flex justify-between px-2 py-1 rounded ${colorClass}`}>
                <span className='text-gray-700 dark:text-gray-300'>{r.label}</span>
                <span className='font-bold'>
                  {r.format ? r.format(data[r.key]) : formatNumber(data[r.key])}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const CompanionCard = ({ data }) => {
    if (!data) return <p className='text-gray dark:text-gray-400'>Sin datos</p>
    const rows = [
      { label: 'Compras', key: 'purchases' },
      { label: 'Completados', key: 'completed' },
      { label: 'Cancelados', key: 'cancelled' },
      { label: 'Abandonos', key: 'abandonments' },
      { label: 'Conversión', key: 'conversion', format: formatPercent },
      { label: 'Tasa de cancelación', key: 'cancellation_rate', format: formatPercent },
      { label: 'Km', key: 'km' },
      { label: 'Tasa de abandono', key: 'abandonment_rate', format: formatPercent }
    ]
    return (
      <div className='border-[1px] border-gray rounded-lg p-5 dark:border-dk-border'>
        <p className='font-bold mb-3'>Acompañante</p>
        <div className='flex flex-col gap-2'>
          {rows.map(r => (
            <div key={r.key} className='flex justify-between'>
              <span className='text-gray-700 dark:text-gray-300'>{r.label}</span>
              <span className='font-bold'>
                {r.format ? r.format(data[r.key]) : formatNumber(data[r.key])}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='border-[1px] border-gray rounded-lg p-5 mb-6 dark:border-dk-border'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-4'>
          <p className='font-bold text-lg'>Estadísticas</p>
          {loading && <LoadingIndicator size={16} />}
        </div>
        <Select
          size='small'
          value={range}
          onChange={e => setRange(e.target.value)}
          disabled={available.length === 0}
        >
          {available.map(k => (
            <MenuItem key={k} value={k}>
              {labelForRange(k)}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <DriverCard data={driverStats?.[range]} />
        <CompanionCard data={companionStats?.[range]} />
      </div>
    </div>
  )
}
